<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useInventoryStore } from '../stores/inventory'
import { useConsumablesStore, type Consumable, type CreateConsumableRequest, type ConsumableCategory } from '../stores/consumables'
import ConsumableCard from '../components/ConsumableCard.vue'
import PhotoUploader from '../components/PhotoUploader.vue'

const authStore = useAuthStore()
const inventoryStore = useInventoryStore()
const consumablesStore = useConsumablesStore()
const router = useRouter()

// Filter state
const selectedCategory = ref<ConsumableCategory | ''>('')
const showLowStockOnly = ref(false)

// Modal state
const showAddModal = ref(false)
const showUseModal = ref(false)
const showRestockModal = ref(false)
const showDetailModal = ref(false)
const selectedConsumable = ref<Consumable | null>(null)

// Form state
const formData = ref<CreateConsumableRequest>({
  name: '',
  description: '',
  category: 'cleaning',
  location_id: '',
  unit: '',
  current_quantity: 0,
  min_quantity: 1,
  max_quantity: 10,
  photo_key: '',
  purchase_link: '',
  notes: '',
})
const formError = ref('')
const saving = ref(false)

// Usage/restock form
const quantityInput = ref(1)
const notesInput = ref('')

const filteredConsumables = computed(() => {
  let items = consumablesStore.consumables

  if (selectedCategory.value) {
    items = items.filter(c => c.category === selectedCategory.value)
  }

  if (showLowStockOnly.value) {
    items = items.filter(c => c.current_quantity <= c.min_quantity)
  }

  return items
})

const groupedConsumables = computed(() => {
  const groups: Record<ConsumableCategory, Consumable[]> = {
    cleaning: [],
    toiletries: [],
    kitchen: [],
    outdoor: [],
  }

  for (const item of filteredConsumables.value) {
    groups[item.category].push(item)
  }

  return groups
})

const categories: ConsumableCategory[] = ['cleaning', 'toiletries', 'kitchen', 'outdoor']

onMounted(async () => {
  await Promise.all([
    inventoryStore.fetchCategories(),
    consumablesStore.fetchConsumables(),
  ])
})

function openAddModal() {
  selectedConsumable.value = null
  formData.value = {
    name: '',
    description: '',
    category: 'cleaning',
    location_id: '',
    unit: '',
    current_quantity: 0,
    min_quantity: 1,
    max_quantity: 10,
    photo_key: '',
    purchase_link: '',
    notes: '',
  }
  formError.value = ''
  showAddModal.value = true
}

function openEditModal(consumable: Consumable) {
  selectedConsumable.value = consumable
  formData.value = {
    name: consumable.name,
    description: consumable.description || '',
    category: consumable.category,
    location_id: consumable.location_id || '',
    unit: consumable.unit || '',
    current_quantity: consumable.current_quantity,
    min_quantity: consumable.min_quantity,
    max_quantity: consumable.max_quantity,
    photo_key: consumable.photo_key || '',
    purchase_link: consumable.purchase_link || '',
    notes: consumable.notes || '',
  }
  formError.value = ''
  showAddModal.value = true
}

function closeModal() {
  showAddModal.value = false
  showUseModal.value = false
  showRestockModal.value = false
  showDetailModal.value = false
  selectedConsumable.value = null
  quantityInput.value = 1
  notesInput.value = ''
}

async function saveConsumable() {
  if (!formData.value.name) {
    formError.value = 'Name is required'
    return
  }

  saving.value = true
  formError.value = ''

  try {
    if (selectedConsumable.value) {
      await consumablesStore.updateConsumable(selectedConsumable.value.id, formData.value)
    } else {
      await consumablesStore.createConsumable(formData.value)
    }
    closeModal()
  } catch (err) {
    formError.value = err instanceof Error ? err.message : 'Failed to save'
  } finally {
    saving.value = false
  }
}

function openUseModal(consumable: Consumable) {
  selectedConsumable.value = consumable
  quantityInput.value = 1
  notesInput.value = ''
  showUseModal.value = true
}

async function recordUsage() {
  if (!selectedConsumable.value || quantityInput.value <= 0) return

  try {
    await consumablesStore.recordUsage(selectedConsumable.value.id, quantityInput.value, notesInput.value || undefined)
    closeModal()
  } catch (err) {
    console.error('Failed to record usage:', err)
  }
}

function openRestockModal(consumable: Consumable) {
  selectedConsumable.value = consumable
  quantityInput.value = consumable.max_quantity - consumable.current_quantity
  notesInput.value = ''
  showRestockModal.value = true
}

async function recordRestock() {
  if (!selectedConsumable.value || quantityInput.value <= 0) return

  try {
    await consumablesStore.recordRestock(selectedConsumable.value.id, quantityInput.value, notesInput.value || undefined)
    closeModal()
  } catch (err) {
    console.error('Failed to record restock:', err)
  }
}

async function openDetailModal(consumable: Consumable) {
  const result = await consumablesStore.fetchConsumable(consumable.id)
  if (result) {
    selectedConsumable.value = result.consumable
    showDetailModal.value = true
  }
}

async function deleteConsumable() {
  if (!selectedConsumable.value) return

  if (confirm(`Delete "${selectedConsumable.value.name}"? This cannot be undone.`)) {
    try {
      await consumablesStore.deleteConsumable(selectedConsumable.value.id)
      closeModal()
    } catch (err) {
      console.error('Failed to delete:', err)
    }
  }
}

function handlePhotoUploaded(key: string) {
  formData.value.photo_key = key
}

function handlePhotoRemoved() {
  formData.value.photo_key = ''
}

function handlePhotoError(message: string) {
  formError.value = message
}

const currentPhotoUrl = computed(() => {
  if (formData.value.photo_key) {
    return `/api/consumables/photo/${formData.value.photo_key}`
  }
  return selectedConsumable.value?.photo_url || null
})

const API_BASE = import.meta.env.VITE_API_URL || ''
</script>

<template>
  <div class="max-w-7xl mx-auto p-4 md:p-6">
    <!-- Header -->
    <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
      <div>
        <h1 class="text-2xl font-bold text-gray-800">Consumables</h1>
        <p class="text-gray-600">Track shared supplies and household items</p>
      </div>
      <div class="flex gap-3">
        <button
          v-if="authStore.canManageInventory"
          @click="openAddModal"
          class="btn btn-primary"
        >
          + Add Consumable
        </button>
      </div>
    </div>

    <!-- Low Stock Alert -->
    <div
      v-if="consumablesStore.lowStock.length > 0"
      class="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6"
    >
      <div class="flex items-start gap-3">
        <svg class="w-6 h-6 text-orange-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div>
          <h3 class="font-medium text-orange-800">Low Stock Items</h3>
          <p class="text-orange-700 text-sm">
            {{ consumablesStore.lowStock.length }} item{{ consumablesStore.lowStock.length !== 1 ? 's' : '' }} running low:
            {{ consumablesStore.lowStock.map(c => c.name).join(', ') }}
          </p>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="card mb-6">
      <div class="flex flex-wrap gap-4 items-center">
        <select v-model="selectedCategory" class="input w-auto">
          <option value="">All Categories</option>
          <option v-for="cat in categories" :key="cat" :value="cat">
            {{ consumablesStore.categoryLabels[cat] }}
          </option>
        </select>
        <label class="flex items-center gap-2 cursor-pointer">
          <input v-model="showLowStockOnly" type="checkbox" class="w-4 h-4" />
          <span class="text-sm text-gray-700">Low stock only</span>
        </label>
        <span class="text-gray-500 text-sm ml-auto">
          {{ filteredConsumables.length }} item{{ filteredConsumables.length !== 1 ? 's' : '' }}
        </span>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="consumablesStore.loading" class="text-center py-12">
      <p class="text-gray-500">Loading consumables...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredConsumables.length === 0" class="text-center py-12">
      <p class="text-gray-500 mb-4">No consumables found</p>
      <button
        v-if="authStore.canManageInventory"
        @click="openAddModal"
        class="btn btn-primary"
      >
        Add First Consumable
      </button>
    </div>

    <!-- Grouped by Category -->
    <div v-else class="space-y-8">
      <div v-for="cat in categories" :key="cat">
        <template v-if="!selectedCategory || selectedCategory === cat">
          <template v-if="groupedConsumables[cat].length > 0">
            <h2 class="text-lg font-semibold text-gray-700 mb-4 flex items-center gap-2">
              {{ consumablesStore.categoryLabels[cat] }}
              <span class="text-sm font-normal text-gray-400">
                ({{ groupedConsumables[cat].length }})
              </span>
            </h2>
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <ConsumableCard
                v-for="consumable in groupedConsumables[cat]"
                :key="consumable.id"
                :consumable="consumable"
                :can-manage="authStore.canManageInventory"
                :can-use="authStore.canUpdateConsumables"
                @click="openDetailModal(consumable)"
                @use="openUseModal(consumable)"
                @restock="openRestockModal(consumable)"
                @edit="openEditModal(consumable)"
              />
            </div>
          </template>
        </template>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div
      v-if="showAddModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto"
      @click.self="closeModal"
    >
      <div class="card max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div class="flex justify-between items-start mb-4">
          <h2 class="text-xl font-bold text-gray-800">
            {{ selectedConsumable ? 'Edit Consumable' : 'Add New Consumable' }}
          </h2>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>

        <div v-if="formError" class="bg-red-50 text-red-600 p-3 rounded-lg mb-4">
          {{ formError }}
        </div>

        <form @submit.prevent="saveConsumable" class="space-y-4">
          <!-- Photo -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">Photo</label>
            <PhotoUploader
              :current-photo-url="currentPhotoUrl"
              upload-endpoint="consumables"
              @uploaded="handlePhotoUploaded"
              @removed="handlePhotoRemoved"
              @error="handlePhotoError"
            />
          </div>

          <!-- Basic Info -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input v-model="formData.name" type="text" class="input" required />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Category *</label>
              <select v-model="formData.category" class="input" required>
                <option v-for="cat in categories" :key="cat" :value="cat">
                  {{ consumablesStore.categoryLabels[cat] }}
                </option>
              </select>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <select v-model="formData.location_id" class="input">
                <option value="">Select location</option>
                <option
                  v-for="loc in inventoryStore.locations"
                  :key="loc.id"
                  :value="loc.id"
                >
                  {{ loc.name }}
                </option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Unit (e.g., rolls, bottles)</label>
              <input v-model="formData.unit" type="text" class="input" placeholder="rolls" />
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea v-model="formData.description" class="input" rows="2"></textarea>
          </div>

          <!-- Quantity Settings -->
          <div class="border-t pt-4">
            <h3 class="font-medium text-gray-700 mb-3">Quantity Settings</h3>
            <div class="grid grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Current</label>
                <input v-model.number="formData.current_quantity" type="number" min="0" class="input" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Min (Alert)</label>
                <input v-model.number="formData.min_quantity" type="number" min="0" class="input" />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Max (Full)</label>
                <input v-model.number="formData.max_quantity" type="number" min="1" class="input" />
              </div>
            </div>
          </div>

          <!-- Additional -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Purchase Link</label>
            <input v-model="formData.purchase_link" type="url" class="input" placeholder="https://..." />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea v-model="formData.notes" class="input" rows="2"></textarea>
          </div>

          <!-- Actions -->
          <div class="flex gap-3 pt-4 border-t">
            <button
              v-if="selectedConsumable && authStore.canManageInventory"
              type="button"
              @click="deleteConsumable"
              class="btn btn-danger"
            >
              Delete
            </button>
            <div class="flex-1"></div>
            <button type="button" @click="closeModal" class="btn">
              Cancel
            </button>
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="saving"
            >
              {{ saving ? 'Saving...' : (selectedConsumable ? 'Save' : 'Add') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Use Modal -->
    <div
      v-if="showUseModal && selectedConsumable"
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      @click.self="closeModal"
    >
      <div class="card max-w-sm w-full">
        <h2 class="text-xl font-bold text-gray-800 mb-4">Record Usage</h2>
        <p class="text-gray-600 mb-4">How much {{ selectedConsumable.name }} did you use?</p>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Quantity {{ selectedConsumable.unit ? `(${selectedConsumable.unit})` : '' }}
            </label>
            <input v-model.number="quantityInput" type="number" min="1" class="input" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <input v-model="notesInput" type="text" class="input" placeholder="e.g., cleaned kitchen" />
          </div>
        </div>

        <div class="flex gap-3 mt-6">
          <button @click="closeModal" class="btn flex-1">Cancel</button>
          <button @click="recordUsage" class="btn btn-primary flex-1">Record</button>
        </div>
      </div>
    </div>

    <!-- Restock Modal -->
    <div
      v-if="showRestockModal && selectedConsumable"
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      @click.self="closeModal"
    >
      <div class="card max-w-sm w-full">
        <h2 class="text-xl font-bold text-gray-800 mb-4">Restock</h2>
        <p class="text-gray-600 mb-4">How much {{ selectedConsumable.name }} are you adding?</p>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Quantity {{ selectedConsumable.unit ? `(${selectedConsumable.unit})` : '' }}
            </label>
            <input v-model.number="quantityInput" type="number" min="1" class="input" />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <input v-model="notesInput" type="text" class="input" placeholder="e.g., bought at Costco" />
          </div>
        </div>

        <div class="flex gap-3 mt-6">
          <button @click="closeModal" class="btn flex-1">Cancel</button>
          <button @click="recordRestock" class="btn btn-primary flex-1">Restock</button>
        </div>
      </div>
    </div>

    <!-- Detail Modal -->
    <div
      v-if="showDetailModal && selectedConsumable"
      class="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      @click.self="closeModal"
    >
      <div class="card max-w-lg w-full max-h-[80vh] overflow-y-auto">
        <div class="flex justify-between items-start mb-4">
          <h2 class="text-xl font-bold text-gray-800">{{ selectedConsumable.name }}</h2>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>

        <!-- Photo -->
        <div v-if="selectedConsumable.photo_url" class="h-48 bg-gray-100 rounded-lg overflow-hidden mb-4">
          <img
            :src="`${API_BASE}${selectedConsumable.photo_url}`"
            :alt="selectedConsumable.name"
            class="w-full h-full object-contain"
          />
        </div>

        <div class="space-y-3 text-gray-600">
          <p v-if="selectedConsumable.description">{{ selectedConsumable.description }}</p>

          <div class="flex flex-wrap gap-2">
            <span class="px-2 py-1 bg-gray-100 text-gray-600 rounded text-sm">
              {{ consumablesStore.categoryLabels[selectedConsumable.category] }}
            </span>
            <span v-if="selectedConsumable.location_name" class="px-2 py-1 bg-gray-100 text-gray-600 rounded text-sm">
              {{ selectedConsumable.location_name }}
            </span>
          </div>

          <!-- Quantity Bar -->
          <div class="pt-2">
            <div class="flex justify-between text-sm mb-1">
              <span>{{ selectedConsumable.current_quantity }} {{ selectedConsumable.unit }}</span>
              <span class="text-gray-400">/ {{ selectedConsumable.max_quantity }}</span>
            </div>
            <div class="h-3 bg-gray-200 rounded-full overflow-hidden">
              <div
                :class="['h-full', consumablesStore.getStockColor(consumablesStore.getStockLevel(selectedConsumable))]"
                :style="{ width: `${(selectedConsumable.current_quantity / selectedConsumable.max_quantity) * 100}%` }"
              ></div>
            </div>
          </div>

          <div v-if="selectedConsumable.purchase_link" class="pt-2">
            <a
              :href="selectedConsumable.purchase_link"
              target="_blank"
              rel="noopener"
              class="text-primary-600 hover:text-primary-700 text-sm inline-flex items-center gap-1"
            >
              Where to buy
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>

          <p v-if="selectedConsumable.notes" class="text-sm text-gray-500 pt-2 border-t">
            {{ selectedConsumable.notes }}
          </p>
        </div>

        <!-- Actions -->
        <div class="flex gap-3 mt-6 pt-4 border-t">
          <button
            v-if="authStore.canUpdateConsumables && selectedConsumable.current_quantity > 0"
            @click="closeModal(); openUseModal(selectedConsumable)"
            class="btn flex-1"
          >
            Used Some
          </button>
          <button
            v-if="authStore.canManageInventory"
            @click="closeModal(); openRestockModal(selectedConsumable)"
            class="btn btn-primary flex-1"
          >
            Restock
          </button>
          <button
            v-if="authStore.canManageInventory"
            @click="closeModal(); openEditModal(selectedConsumable)"
            class="btn"
          >
            Edit
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
